import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, AlertTriangle } from "lucide-react";
import type { UserStory } from "@/lib/projectStore";

interface CPMTask {
  id: string;
  name: string;
  duration: number;
  dependencies: string[];
  // computed
  ES: number; EF: number; LS: number; LF: number; float: number;
}

interface Props { stories?: UserStory[]; }

function computeCPM(tasks: Omit<CPMTask, "ES" | "EF" | "LS" | "LF" | "float">[]): CPMTask[] {
  const map = new Map(tasks.map(t => [t.id, { ...t, ES: 0, EF: 0, LS: 0, LF: 0, float: 0 }]));

  // Forward pass
  let changed = true;
  let iterations = 0;
  while (changed && iterations < 100) {
    changed = false; iterations++;
    map.forEach(task => {
      const ES = task.dependencies.length === 0 ? 0
        : Math.max(...task.dependencies.map(d => map.get(d)?.EF ?? 0));
      const EF = ES + task.duration;
      if (ES !== task.ES || EF !== task.EF) { task.ES = ES; task.EF = EF; changed = true; }
    });
  }

  const projectEnd = Math.max(...Array.from(map.values()).map(t => t.EF));

  // Backward pass
  map.forEach(task => { task.LF = projectEnd; task.LS = projectEnd - task.duration; });
  changed = true; iterations = 0;
  while (changed && iterations < 100) {
    changed = false; iterations++;
    map.forEach(task => {
      const successors = Array.from(map.values()).filter(t => t.dependencies.includes(task.id));
      if (successors.length === 0) return;
      const LF = Math.min(...successors.map(s => s.LS));
      const LS = LF - task.duration;
      if (LF !== task.LF || LS !== task.LS) { task.LF = LF; task.LS = LS; changed = true; }
    });
  }

  map.forEach(task => { task.float = task.LS - task.ES; });
  return Array.from(map.values());
}

export default function CriticalPathPanel({ stories = [] }: Props) {
  const [tasks, setTasks] = useState<Omit<CPMTask, "ES" | "EF" | "LS" | "LF" | "float">[]>([]);
  const [draft, setDraft] = useState({ name: "", duration: 1, dependencies: "" });
  const [useStories, setUseStories] = useState(false);

  const storyTasks = useMemo(() =>
    stories.filter(s => s.startDate && s.dueDate).map(s => ({
      id: s.id, name: s.title,
      duration: Math.max(1, Math.round((new Date(s.dueDate!).getTime() - new Date(s.startDate!).getTime()) / 86400000)),
      dependencies: s.dependencies || [],
    })), [stories]);

  const activeTasks = useStories ? storyTasks : tasks;
  const computed = useMemo(() => computeCPM(activeTasks), [activeTasks]);
  const criticalPath = computed.filter(t => t.float === 0);
  const projectDuration = computed.length > 0 ? Math.max(...computed.map(t => t.EF)) : 0;

  const addTask = () => {
    if (!draft.name.trim()) return;
    setTasks(prev => [...prev, {
      id: crypto.randomUUID(),
      name: draft.name.trim(),
      duration: draft.duration,
      dependencies: draft.dependencies.split(",").map(s => s.trim()).filter(Boolean),
    }]);
    setDraft({ name: "", duration: 1, dependencies: "" });
  };

  const deleteTask = (id: string) => setTasks(prev => prev.filter(t => t.id !== id));

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 border-b border-border px-5 py-3 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Critical Path Method (CPM)</h2>
          <p className="text-[11px] text-muted-foreground">
            {computed.length > 0
              ? `Project duration: ${projectDuration} days · ${criticalPath.length} critical task${criticalPath.length !== 1 ? "s" : ""}`
              : "Find the longest path — zero float tasks cannot slip without delaying the project."}
          </p>
        </div>
        {stories.some(s => s.startDate) && (
          <Button size="sm" variant={useStories ? "default" : "outline"} className="h-7 text-[11px]"
            onClick={() => setUseStories(v => !v)}>
            {useStories ? "Using Timeline stories" : "Import from Timeline"}
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" style={{ scrollbarWidth: "thin" }}>

        {/* Critical path highlight */}
        {criticalPath.length > 0 && (
          <div className="border border-red-300 bg-red-50 dark:bg-red-900/10 rounded-lg p-3 space-y-1">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-red-700 dark:text-red-400">
              <AlertTriangle className="h-3.5 w-3.5" />
              Critical Path — {projectDuration} days. Any delay here delays the project.
            </div>
            <div className="flex flex-wrap gap-1.5">
              {criticalPath.map((t, i) => (
                <span key={t.id} className="text-[11px] flex items-center gap-1">
                  <span className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-2 py-0.5 rounded font-medium">{t.name}</span>
                  {i < criticalPath.length - 1 && <span className="text-red-400">→</span>}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Task table */}
        {!useStories && (
          <div>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground">Task</th>
                    <th className="text-center px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground">Dur (d)</th>
                    <th className="text-center px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground">Depends on</th>
                    <th className="text-center px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground">ES</th>
                    <th className="text-center px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground">EF</th>
                    <th className="text-center px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground">LS</th>
                    <th className="text-center px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground">LF</th>
                    <th className="text-center px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground">Float</th>
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {computed.map(task => (
                    <tr key={task.id} className={`border-b border-border/50 ${task.float === 0 ? "bg-red-50/50 dark:bg-red-900/5" : "hover:bg-muted/20"}`}>
                      <td className="px-3 py-2 font-medium flex items-center gap-1.5">
                        {task.float === 0 && <span className="h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" />}
                        {task.name}
                      </td>
                      <td className="px-3 py-2 text-center font-mono">{task.duration}d</td>
                      <td className="px-3 py-2 text-center text-muted-foreground text-[11px]">
                        {task.dependencies.map(d => computed.find(t => t.id === d)?.name || d).join(", ") || "—"}
                      </td>
                      <td className="px-3 py-2 text-center font-mono">{task.ES}</td>
                      <td className="px-3 py-2 text-center font-mono">{task.EF}</td>
                      <td className="px-3 py-2 text-center font-mono">{task.LS}</td>
                      <td className="px-3 py-2 text-center font-mono">{task.LF}</td>
                      <td className="px-3 py-2 text-center">
                        <Badge variant="outline" className={`text-[10px] font-mono ${task.float === 0 ? "bg-red-100 text-red-700 border-red-300 dark:bg-red-900/20 dark:text-red-400" : "bg-green-100 text-green-700 border-green-300"}`}>
                          {task.float}d
                        </Badge>
                      </td>
                      <td className="px-2 py-2">
                        <button onClick={() => deleteTask(task.id)} className="text-muted-foreground hover:text-destructive">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {/* Add row */}
                  <tr className="bg-muted/10">
                    <td className="px-3 py-2">
                      <Input value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                        className="h-6 text-[12px]" placeholder="Task name…" onKeyDown={e => e.key === "Enter" && addTask()} />
                    </td>
                    <td className="px-3 py-2">
                      <Input type="number" min={1} value={draft.duration} onChange={e => setDraft(d => ({ ...d, duration: +e.target.value }))}
                        className="h-6 text-[12px] w-16" />
                    </td>
                    <td className="px-3 py-2" colSpan={5}>
                      <Input value={draft.dependencies} onChange={e => setDraft(d => ({ ...d, dependencies: e.target.value }))}
                        className="h-6 text-[11px]" placeholder="Task IDs or leave blank" />
                    </td>
                    <td className="px-2 py-2">
                      <Button size="sm" variant="outline" className="h-6 px-2" onClick={addTask}><Plus className="h-3 w-3" /></Button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2 font-mono">ES=Early Start · EF=Early Finish · LS=Late Start · LF=Late Finish · Float=slack days before delay impacts project</p>
          </div>
        )}

        {useStories && computed.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground">Story</th>
                  <th className="text-center px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground">Dur</th>
                  <th className="text-center px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground">ES</th>
                  <th className="text-center px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground">EF</th>
                  <th className="text-center px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground">Float</th>
                </tr>
              </thead>
              <tbody>
                {computed.map(task => (
                  <tr key={task.id} className={`border-b border-border/50 ${task.float === 0 ? "bg-red-50/50 dark:bg-red-900/5" : ""}`}>
                    <td className="px-3 py-2 font-medium flex items-center gap-1.5">
                      {task.float === 0 && <span className="h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" />}
                      {task.name}
                    </td>
                    <td className="px-3 py-2 text-center font-mono">{task.duration}d</td>
                    <td className="px-3 py-2 text-center font-mono">{task.ES}</td>
                    <td className="px-3 py-2 text-center font-mono">{task.EF}</td>
                    <td className="px-3 py-2 text-center">
                      <Badge variant="outline" className={`text-[10px] font-mono ${task.float === 0 ? "bg-red-100 text-red-700 border-red-300" : "bg-green-100 text-green-700 border-green-300"}`}>
                        {task.float}d
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {computed.length === 0 && !useStories && (
          <div className="text-center py-12 text-muted-foreground">
            <p className="text-sm font-medium">No tasks yet</p>
            <p className="text-[11px] mt-1">Add tasks above. Leave "Depends on" blank for start tasks.</p>
          </div>
        )}
      </div>
    </div>
  );
}
